import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div>
      <h1>Privacy Policy</h1>
      <p><strong>Last Updated: June 13, 2025</strong></p>

      <h2>Welcome to Our Privacy Policy</h2>

      <h3>1. What is a Privacy Policy?</h3>

      <h4>A. Our Privacy Policy Helps You Understand What We Do With Your Information</h4>
      <p>We collect information from you as you use our board game rules interpretation service at miskatonic.academy (referred to as our "Service" or "website"). This privacy policy explains how we—Miskatonic Academy—use that information and what choices you have about it.</p>
      <p>Our privacy policy applies to our website, whether you access it through a web browser, mobile device, or another method. Our privacy policy does NOT apply to information collected by third parties or other websites you may access through links on our Service.</p>

      <h4>B. Our Privacy Policy is Part of an Agreement Between You and Us</h4>
      <p>Our Privacy Policy and our Terms of Service apply to you when you access our board game rules interpretation service, ask questions about game rules, and otherwise interact with our website.</p>
      <p>Keep in mind that we provide an educational interpretation service. We do not represent board game publishers or claim authority over official game rules.</p>

      <h4>C. Our Privacy Policy Can Change From Time to Time</h4>
      <p>When we make significant changes to our privacy policy, we'll update the date at the top of this page and may provide notice through our website. Your continued use of our Service after we post an updated privacy policy means that you accept and agree to the changes, so please review this page periodically.</p>

      <h2>2. What is Personal Information?</h2>
      <p>"Personal information" means information that can directly or indirectly identify you, such as your name, email address, contact information, and in some cases, information about your behavior on our website.</p>
      <p>"Personal information" does NOT include information that we have made anonymous so that it can no longer be used to identify you. We collect anonymized data from our users for internal statistical purposes and for providing our services (such as question trends, popular games, and usage analytics). We may share this anonymous grouped data with board game publishers to help them improve their rulebooks, but it will not be associated with your identity.</p>

      <h2>3. What Kind of Personal Information Are We Collecting?</h2>
      <p>Since our Service does not require account creation, we collect minimal personal information from you in two ways:</p>
      <ul>
        <li>You give it to us directly (such as through contact forms)</li>
        <li>We get it automatically as you use our Service</li>
      </ul>
      <p>We only collect as much information about you as is reasonably necessary to provide our board game rules interpretation service. The table below summarizes what happens to your information:</p>

      <table>
        <thead>
          <tr>
            <th>What Information Do We Collect?</th>
            <th>When Do We Collect It?</th>
            <th>How Do We Collect It?</th>
            <th>Why Do We Collect It?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Your questions about board game rules</td>
            <td>When you submit questions</td>
            <td>You provide them when using our Service</td>
            <td>To provide you with rule interpretations and improve our educational content</td>
          </tr>
          <tr>
            <td>Technical data (IP address, browser info, device type, operating system)</td>
            <td>When you access our Service</td>
            <td>Automatically collected when you visit our website</td>
            <td>To secure our Service, prevent abuse, remember your preferences, and analyze usage patterns for service improvement</td>
          </tr>
          <tr>
            <td>Usage behavior (pages visited, questions asked, games queried)</td>
            <td>When you use our Service</td>
            <td>Automatically collected during your use of our Service</td>
            <td>To improve our Service, understand user needs, and create anonymized statistics for potential sharing with game publishers</td>
          </tr>
          <tr>
            <td>Contact information (if provided)</td>
            <td>When you contact our support</td>
            <td>You provide it when reaching out to us</td>
            <td>To respond to your inquiries and provide customer support</td>
          </tr>
          <tr>
            <td>Analytics data (page views, session duration, user interactions)</td>
            <td>When you use our Service</td>
            <td>Automatically collected through Google Analytics</td>
            <td>To understand how users interact with our Service, improve user experience, and analyze website performance</td>
          </tr>
          <tr>
            <td>Advertising data (ad interactions, interests, demographics)</td>
            <td>When you view or interact with ads on our Service</td>
            <td>Automatically collected through Google AdSense and advertising partners</td>
            <td>To display relevant advertisements and measure ad performance</td>
          </tr>
        </tbody>
      </table>

      <h2>4. What About the Content You Submit?</h2>
      <p>When you ask questions about board game rules through our Service, that content is stored to:</p>
      <ul>
        <li>Provide you with accurate rule interpretations</li>
        <li>Improve our educational content and interpretation accuracy</li>
        <li>Create anonymized statistics about common rule questions</li>
      </ul>
      <p>Your questions may reference specific board games, rule situations, or gameplay scenarios. While this content helps us provide better service, you should avoid including sensitive personal information in your rule questions.</p>

      <h2>5. Rights for Users in Different Jurisdictions</h2>

      <h3>A. For Users Covered by the EU's General Data Protection Regulation (GDPR)</h3>

      <h4>Your Rights Under GDPR</h4>
      <p>You have rights regarding your personal information, including the right to:</p>
      <ul>
        <li>Access your information</li>
        <li>Correct or update your information</li>
        <li>Erase your information</li>
        <li>Restrict our use of your personal information under certain circumstances</li>
        <li>Obtain your personal information in portable form</li>
        <li>Object to our use of your personal information under certain circumstances</li>
        <li>Not be subject to automated decision-making that has legal effects on you</li>
        <li>Withdraw consent for analytics and advertising cookies at any time</li>
      </ul>

      <h4>Our Legal Basis for Using Your Information</h4>
      <p>We collect and use your personal information based on:</p>
      <p><strong>With Your Consent:</strong> When you use our Service, you consent to our collection and use of your information for:</p>
      <ul>
        <li>Providing board game rules interpretation services</li>
        <li>Improving our educational content</li>
        <li>Creating anonymized statistics for potential sharing with publishers</li>
        <li>Analytics tracking through Google Analytics (with your cookie consent)</li>
        <li>Displaying personalized advertisements through Google AdSense (with your cookie consent)</li>
      </ul>
      <p><strong>For Our Legitimate Interests:</strong> We may use your information for legitimate business interests that don't override your privacy rights, including:</p>
      <ul>
        <li>Improving our Service and user experience</li>
        <li>Preventing fraud and abuse</li>
        <li>Analyzing usage trends to enhance our educational offerings</li>
        <li>Ensuring the security and stability of our Service</li>
        <li>Generating revenue through appropriate advertising to support our free educational service</li>
      </ul>
      <p><strong>Legal Compliance:</strong> We may use your information to comply with applicable laws and regulations.</p>

      <h3>B. For California Residents</h3>
      <p>Under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), you have the right to:</p>
      <ul>
        <li>Know what personal information we collect about you</li>
        <li>Know whether we sell or share your personal information (we do not sell, but may share with Google for advertising purposes)</li>
        <li>Access your personal information</li>
        <li>Delete your personal information</li>
        <li>Correct inaccurate personal information</li>
        <li>Opt-out of the sale or sharing of your personal information</li>
        <li>Not be discriminated against for exercising your privacy rights</li>
      </ul>
      <p><strong>Important:</strong> We do not sell your personal information to third parties. However, our use of Google AdSense may constitute "sharing" under CCPA definitions, as Google uses your information to personalize ads.</p>

      <h3>C. For Canadian Residents</h3>
      <p>Under Canadian privacy laws (including PIPEDA), you have the right to:</p>
      <ul>
        <li>Access your personal information</li>
        <li>Correct inaccurate personal information</li>
        <li>Withdraw consent for certain uses of your information, including analytics and advertising cookies</li>
        <li>Receive information about our privacy practices</li>
      </ul>
      <p>We are responsible for protecting your information even when we work with third parties, and we ensure such parties provide adequate protection.</p>

      <h2>6. How Long Do We Store Your Information?</h2>
      <ul>
        <li><strong>Contact inquiries:</strong> We store any personal information collected from contact forms only as long as necessary to respond to your inquiry</li>
        <li><strong>Technical data:</strong> Technical data may be retained for up to 18 months for security and analytics purposes</li>
        <li><strong>Questions and content:</strong> Questions you submit may be retained in anonymized form to improve our educational content</li>
        <li><strong>Google Analytics data:</strong> Analytics data is retained according to Google's data retention policies (default 26 months, configurable by us)</li>
        <li><strong>Google AdSense data:</strong> Advertising data is retained according to Google's advertising data retention policies</li>
        <li><strong>Upon request for deletion:</strong> We will delete your personal information within 30 days of a valid deletion request, though anonymized usage statistics may be retained. Note that data processed by Google may be subject to Google's own data retention and deletion policies</li>
      </ul>

      <h2>7. Will We Contact You?</h2>
      <p>We will contact you by email only if:</p>
      <ul>
        <li>You have provided your email address through a contact form</li>
        <li>You have requested a response to your inquiry</li>
        <li>We need to respond to your support requests</li>
      </ul>

      <h3>What We Will NOT Do</h3>
      <p>We will NOT:</p>
      <ul>
        <li>Send you marketing emails without your explicit consent</li>
        <li>Share your email address with third parties for their marketing purposes</li>
        <li>Send you promotional content from board game publishers or other companies</li>
        <li>Add you to mailing lists unless you specifically opt-in</li>
      </ul>

      <h2>8. Tracking, Cookies, and Third-Party Services</h2>

      <h3>A. What We Track</h3>
      <p>We use cookies and similar technologies to:</p>
      <ul>
        <li>Remember your preferences and cookie consent choices</li>
        <li>Analyze how our Service is used through Google Analytics</li>
        <li>Display relevant advertisements through Google AdSense</li>
        <li>Improve website performance and security</li>
        <li>Provide basic functionality</li>
      </ul>

      <h3>B. Google Analytics</h3>
      <p>We use Google Analytics to understand how visitors interact with our website. Google Analytics collects information such as:</p>
      <ul>
        <li>Pages you visit and time spent on each page</li>
        <li>How you arrived at our website</li>
        <li>Your approximate geographic location (country/region level)</li>
        <li>Your device and browser information</li>
        <li>Your interactions with our content</li>
      </ul>
      <p>Google Analytics uses cookies including _ga and _ga_[container-id] that expire after 2 years. You can opt-out of Google Analytics by:</p>
      <ul>
        <li>Using our cookie consent banner to decline analytics cookies</li>
        <li>Installing the Google Analytics Opt-out Browser Add-on</li>
        <li>Adjusting your browser settings to block cookies</li>
      </ul>
      <p>Learn more about Google Analytics privacy at: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></p>

      <h3>C. Google AdSense</h3>
      <p>We use Google AdSense to display advertisements on our website to help support our free educational service. Google AdSense may:</p>
      <ul>
        <li>Use cookies to serve ads based on your prior visits to our website or other websites</li>
        <li>Collect information about your interests to show more relevant ads</li>
        <li>Use your IP address to determine your approximate location for geographic ad targeting</li>
        <li>Share information with advertising partners within Google's advertising network</li>
      </ul>
      <p>You can control personalized advertising by:</p>
      <ul>
        <li>Using our cookie consent banner to decline advertising cookies</li>
        <li>Visiting Google's Ad Settings at <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">https://adssettings.google.com</a></li>
        <li>Visiting the Network Advertising Initiative opt-out page</li>
        <li>Using browser extensions that block advertising trackers</li>
      </ul>
      <p>Learn more about Google's advertising practices at: <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/ads</a></p>

      <h3>D. Your Choices About Tracking</h3>
      <p>You can control tracking through:</p>
      <ul>
        <li><strong>Our Cookie Banner:</strong> When you first visit our site, you can choose which types of cookies to accept</li>
        <li><strong>Browser Settings:</strong> Block or delete cookies through your browser settings</li>
        <li><strong>Browser Extensions:</strong> Use privacy-focused browser extensions to control tracking</li>
        <li><strong>Privacy Settings:</strong> Adjust your browser's privacy and security settings</li>
      </ul>
      <p>Note that blocking certain cookies may affect your ability to use some features of our Service and may result in less relevant advertisements.</p>

      <h3>E. Cookie Categories</h3>
      <p>We categorize cookies as follows:</p>
      <ul>
        <li><strong>Essential Cookies:</strong> Required for basic website functionality (always active)</li>
        <li><strong>Analytics Cookies:</strong> Used for Google Analytics to understand website usage (optional, requires consent)</li>
        <li><strong>Advertising Cookies:</strong> Used for Google AdSense to display relevant ads (optional, requires consent)</li>
      </ul>

      <h3>F. Do Not Track Signals</h3>
      <p>We do not currently respond to "Do Not Track" signals from browsers, as there is no universal standard for how websites should handle these signals. However, you can use our cookie consent options and the opt-out methods described above to control tracking.</p>

      <h2>9. Who Else Do We Share Your Information With?</h2>

      <h3>A. Service Providers</h3>
      <p>We may share limited information with trusted service providers who help us operate our Service, such as:</p>
      <ul>
        <li>Web hosting services</li>
        <li>Security monitoring services</li>
      </ul>

      <h3>B. Google Services</h3>
      <p>We share information with Google through our use of:</p>
      <ul>
        <li><strong>Google Analytics:</strong> Website usage data to understand user behavior and improve our Service</li>
        <li><strong>Google AdSense:</strong> Website content and user interaction data to display relevant advertisements</li>
      </ul>
      <p>Google's use of this information is governed by Google's Privacy Policy and their respective service terms. Google may use this information across their services and for their own business purposes.</p>

      <h3>C. Board Game Publishers (Anonymized Statistics Only)</h3>
      <p>We may share anonymized, aggregated statistics with board game publishers, such as:</p>
      <ul>
        <li>Which games generate the most rule questions</li>
        <li>Common types of rule confusion</li>
        <li>Popular game mechanics that need clarification</li>
      </ul>
      <p>This information will NEVER include your personal information or specific questions you've asked.</p>

      <h3>D. Legal Requirements</h3>
      <p>We may share your information if required by law, such as:</p>
      <ul>
        <li>Complying with legal processes (subpoenas, court orders)</li>
        <li>Protecting our rights and property</li>
        <li>Preventing fraud or abuse</li>
        <li>Ensuring user safety</li>
      </ul>

      <h3>E. Business Transfers</h3>
      <p>If our business is sold or transferred, your personal information may be transferred to the new owner, who will be bound by this privacy policy.</p>

      <h3>F. With Your Permission</h3>
      <p>We will ask for your explicit permission before sharing your information for any other purposes.</p>

      <h2>10. International Data Transfers</h2>
      <p>Our Service is operated from the United States. If you are located outside the US, your information will be transferred to and processed in the United States. Additionally, Google Analytics and AdSense may transfer your data internationally as part of Google's global operations.</p>
      <p>We ensure appropriate safeguards are in place for international data transfers, particularly for users in the EU. Google has implemented adequate safeguards for international transfers, including Standard Contractual Clauses and other approved mechanisms.</p>

      <h2>11. How to Access, Change, or Delete Your Information</h2>

      <h3>A. Requesting Information</h3>
      <p>You can request a copy of the personal information we have about you by contacting us at miskatonic.academy@gmail.com. For information collected by Google Analytics or AdSense, you may need to contact Google directly or use Google's data export tools.</p>

      <h3>B. Deleting Your Information</h3>
      <p>You can request deletion of your personal information by contacting us at miskatonic.academy@gmail.com. We will remove your personal information within 30 days, though anonymized usage statistics may be retained. For data processed by Google services, deletion may be subject to Google's own data retention policies.</p>

      <h3>C. Managing Google Data</h3>
      <p>For data collected by Google services, you can:</p>
      <ul>
        <li>Visit <a href="https://myaccount.google.com" target="_blank" rel="noopener noreferrer">Google My Account</a> to manage your Google data</li>
        <li>Use <a href="https://takeout.google.com" target="_blank" rel="noopener noreferrer">Google Takeout</a> to export your data</li>
        <li>Visit <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google Ad Settings</a> to manage advertising preferences</li>
      </ul>

      <h3>D. Data Portability</h3>
      <p>You can request your personal information in a portable format by contacting us.</p>

      <h2>12. Security</h2>
      <p>We implement appropriate security measures to protect your personal information, including:</p>
      <ul>
        <li>Secure data transmission (HTTPS)</li>
        <li>Regular security updates and monitoring</li>
        <li>Access controls and authentication</li>
        <li>Data encryption for sensitive information</li>
      </ul>
      <p>Google Analytics and AdSense also implement their own security measures to protect data processed through their services.</p>
      <p>However, no internet transmission is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>

      <h2>13. Age Restrictions</h2>
      <p>Our Service is intended for users aged 18 and older. We do not knowingly collect personal information from users under 18. If you are under 18, please do not use our Service or provide any personal information. Google's services also have their own age restrictions which apply to data collection through Analytics and AdSense.</p>

      <h2>14. Changes to This Privacy Policy</h2>
      <p>We may update this privacy policy from time to time. When we make significant changes, we will:</p>
      <ul>
        <li>Update the "Last Updated" date at the top of this policy</li>
        <li>Provide notice on our website</li>
        <li>For material changes, we may provide additional notification methods</li>
        <li>Update our cookie consent banner to reflect any changes in cookie usage</li>
      </ul>
      <p>Your continued use of our Service after changes become effective constitutes acceptance of the updated policy.</p>

      <h2>15. Contact Information</h2>
      <p>If you have questions about this privacy policy or want to exercise your privacy rights, please contact us:</p>
      <p><strong>Email:</strong> miskatonic.academy@gmail.com</p>
      <p><strong>Subject Lines:</strong></p>
      <ul>
        <li>General privacy inquiries: "Privacy Policy Inquiry"</li>
        <li>GDPR-related requests: "GDPR Request"</li>
        <li>CCPA-related requests: "CCPA Request"</li>
        <li>Canadian privacy requests: "Canadian Privacy Request"</li>
        <li>Data deletion requests: "Data Deletion Request"</li>
        <li>Cookie consent inquiries: "Cookie Consent Inquiry"</li>
      </ul>

      <h2>16. Dispute Resolution</h2>
      <p><strong>For US Users:</strong> Any disputes related to this privacy policy will be resolved according to the dispute resolution procedures outlined in our Terms of Service.</p>
      <p><strong>For EU Users:</strong> You have the right to lodge a complaint with your local data protection authority if you believe we have not handled your personal information in accordance with applicable law.</p>
      <p><strong>For Canadian Users:</strong> You may file a complaint with the Privacy Commissioner of Canada if you believe we have violated Canadian privacy laws.</p>

      <p>This Privacy Policy is effective as of June 13, 2025 and supersedes all previous versions.</p>
    </div>
  );
};

export default PrivacyPolicy;